// /components/pages/sign-up-call-request/persistNewRequestCart.ts
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import type { ServiceSlug, FormSlot } from '@/store/newRequestCartStore'
import { serviceConfig } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { makeBlankSlot } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { PendingAcquisitionUser } from '@/dataV3/User/PendingAcquisitionUser'

// adapt this if your Signup store uses different slugs (walk vs promenade etc)
const normalizeServiceSlug = (s?: string): ServiceSlug | null => {
  if (!s) return null
  // if already NewRequest slug
  if (s === 'promenade' || s === 'visite' || s === 'garde-chez-vous' || s === 'garde-chez-eux') return s
  // if signup-like slug
  const map: Record<string, ServiceSlug> = {
    walk: 'promenade',
    homeVisit: 'visite',
    sittingAtCustomerHome: 'garde-chez-vous',
    sittingAtPetsitterHome: 'garde-chez-eux',
  }
  return map[s] ?? null
}

const buildSlotsByService = (selected: ServiceSlug | null, rawSlots: any): Record<ServiceSlug, FormSlot[]> => {
  const next = {
    promenade: [makeBlankSlot('promenade', null)],
    'garde-chez-vous': [makeBlankSlot('garde-chez-vous', null)],
    'garde-chez-eux': [makeBlankSlot('garde-chez-eux', null)],
    visite: [makeBlankSlot('visite', null)],
  } as Record<ServiceSlug, FormSlot[]>

  // Your signup store currently has: service?.formSlots?: any[]
  // but NewRequest expects Record<ServiceSlug, FormSlot[]>
  // If you later change signup to store the record, this will still work.

  const isRecord =
    rawSlots &&
    typeof rawSlots === 'object' &&
    !Array.isArray(rawSlots) &&
    ('promenade' in rawSlots || 'visite' in rawSlots || 'garde-chez-vous' in rawSlots || 'garde-chez-eux' in rawSlots)

  if (isRecord) {
    const restored = rawSlots as Partial<Record<ServiceSlug, FormSlot[]>>
    ;(Object.keys(serviceConfig) as ServiceSlug[]).forEach((slug) => {
      const arr = restored[slug]
      next[slug] = Array.isArray(arr) && arr.length ? arr : [makeBlankSlot(slug, null)]
    })
  } else if (Array.isArray(rawSlots) && selected) {
    // signup store kept a single array of slots for the selected service
    next[selected] = rawSlots.length ? rawSlots : [makeBlankSlot(selected, null)]
  }

  // guarantee recurrent has exactly one slot
  ;(['promenade', 'visite'] as ServiceSlug[]).forEach((slug) => {
    next[slug] = [next[slug]?.[0] ?? makeBlankSlot(slug, null)]
  })

  return next
}

export function persistNewRequestCartFromSignupStore(currentUser?: PendingAcquisitionUser) {
  const signup = useSignUpCallRequestStore.getState()
  const cart = useNewRequestCartStore.getState()

  const selectedService = normalizeServiceSlug(signup.service?.selectedService)
  const formSlotsByService = buildSlotsByService(selectedService, signup.service?.formSlots)

  // Determine animals: use currentUser.animals if available, else signup.animals
  const animals = currentUser?.animals?.length
    ? currentUser.animals.map((a: any) => a.id)
    : signup.animals?.map((a: any) => a.id)

  cart.reset()

  cart.initFromSignUpDraft({
    address: signup.address,
    distance: signup.distance,
    icon: signup.icon,
    message: signup.message,
    additionalInformations: signup.additionalInformations,
    animals,
    service: {
      selectedService: selectedService ?? undefined,
      formSlots: formSlotsByService,
    },
  })

  cart.setInitialized(true)
  // Optional: once copied, clean signup draft
  // signup.reset()
}
